// Die Upload-Fläche im Hero der Startseite (home/_guest_upload.html.erb).
//
// Bild hineinziehen oder auswählen, und es läuft derselbe Gast-Weg wie auf
// /ausprobieren: `lib/guestTrace.ts` schickt die Datei an
// `POST /api/v1/guest/traces`, legt Dokument und Bild in die IndexedDB und
// diese Datei navigiert auf /ausprobieren/ergebnis, wo `GuestResult.tsx` das
// Ergebnis von dort holt.
//
// **Vanilla-TS im Einstiegsbündel, kein React.** Die Startseite lädt
// vertraglich genau ein Bündel (e2e/code-splitting.spec.ts) -- sie ist die
// meistbesuchte Seite und die wichtigste für die Suche, und ein React-Chunk
// für einen Dateiwähler wäre der falsche Preis. Deshalb auch keine Sätze aus
// `../i18n`: die Übersetzungstabelle des Editors gehört nicht in dieses
// Bündel. Die Sätze rendert der Server aus den Locales in `data-messages`
// (`home.upload.messages.*`, de und en), mit denselben Schlüsseln wie die
// Fehlercodes aus lib/guestTrace.ts.
//
// Ohne Skript ist die Fläche ein Dateiwähler ohne Wirkung; der Server rendert
// deshalb den Link auf /ausprobieren mit (`data-guest-upload-fallback`), und
// hier verschwindet er, sobald die Fläche übernommen ist.

import { traceAsGuest, type GuestTraceFailureCode, type GuestTraceOutcome } from '../lib/guestTrace'

export const GUEST_UPLOAD_SELECTOR = '[data-guest-upload]'

const BUSY_CLASS = 'landing-upload--busy'
const DRAGGING_CLASS = 'landing-upload--dragging'
const DEFAULT_RESULT_PATH = '/ausprobieren/ergebnis'

type Messages = Partial<Record<GuestTraceFailureCode | 'tracing', string>>

export type GuestUploadDeps = {
  /** Nur für vitest: die Seite selbst schickt wirklich (lib/guestTrace.ts). */
  trace?: (file: File) => Promise<GuestTraceOutcome>
  /** Nur für vitest: sonst wird wirklich navigiert. */
  navigate?: (path: string) => void
}

function parseMessages(raw: string | undefined): Messages {
  if (!raw) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    return parsed !== null && typeof parsed === 'object' ? (parsed as Messages) : {}
  } catch {
    return {}
  }
}

function carriesFiles(event: Event): boolean {
  const transfer = (event as DragEvent).dataTransfer
  return transfer !== null && transfer !== undefined && Array.from(transfer.types ?? []).includes('Files')
}

function droppedFile(event: Event): File | null {
  const transfer = (event as DragEvent).dataTransfer
  // Nur die erste: ein Gast hat einen Versuch, und ein Stapel wäre eine
  // Frage, die die Fläche nicht stellen kann.
  return transfer?.files?.[0] ?? null
}

/** Eine Fläche verdrahten. Öffentlich, damit vitest sie mit Attrappen füttern kann. */
export function bindGuestUpload(root: HTMLElement, deps: GuestUploadDeps = {}): void {
  const trace = deps.trace ?? ((file: File) => traceAsGuest(file))
  const navigate = deps.navigate ?? ((path: string) => window.location.assign(path))

  const messages = parseMessages(root.dataset.messages)
  const resultPath = root.dataset.resultPath || DEFAULT_RESULT_PATH

  const zone = root.querySelector<HTMLElement>('[data-guest-upload-zone]') ?? root
  const input = root.querySelector<HTMLInputElement>('input[type="file"]')
  const status = root.querySelector<HTMLElement>('[data-guest-upload-status]')
  const failure = root.querySelector<HTMLElement>('[data-guest-upload-failure]')
  const error = root.querySelector<HTMLElement>('[data-guest-upload-error]')
  const retry = root.querySelector<HTMLButtonElement>('[data-guest-upload-retry]')
  const signup = root.querySelector<HTMLElement>('[data-guest-upload-signup]')
  const fallback = root.querySelector<HTMLElement>('[data-guest-upload-fallback]')

  let busy = false
  // Die letzte Datei, für „noch einmal versuchen": bei vollem Topf oder
  // Netzfehler soll niemand seine Datei ein zweites Mal suchen müssen.
  let lastFile: File | null = null

  const setState = (state: 'idle' | 'tracing' | 'done' | 'failed'): void => {
    root.dataset.guestUploadState = state
  }

  const showFailure = (outcome: Extract<GuestTraceOutcome, { ok: false }>): void => {
    if (error) error.textContent = messages[outcome.code] ?? messages.failed ?? outcome.code
    if (retry) retry.hidden = !outcome.retryable
    // Ein verbrauchtes Kontingent ist kein Fehler, den ein zweiter Versuch
    // heilt -- dafür steht das Konto da.
    if (signup) signup.hidden = outcome.code !== 'limit'
    if (failure) failure.hidden = false
  }

  const handle = async (file: File): Promise<void> => {
    if (busy) return
    busy = true
    lastFile = file

    setState('tracing')
    root.classList.add(BUSY_CLASS)
    if (input) input.disabled = true
    if (failure) failure.hidden = true
    if (status) {
      status.textContent = messages.tracing ?? ''
      status.hidden = false
    }

    const outcome = await trace(file)

    if (outcome.ok) {
      // Beschäftigt bleiben: die Navigation kommt gleich, und bis dahin soll
      // die Fläche keine zweite Datei annehmen.
      setState('done')
      navigate(resultPath)
      return
    }

    busy = false
    setState('failed')
    root.classList.remove(BUSY_CLASS)
    if (input) input.disabled = false
    if (status) status.hidden = true
    showFailure(outcome)
  }

  input?.addEventListener('change', () => {
    const file = input.files?.[0] ?? null
    // Zurücksetzen, damit dieselbe Datei nach einem Fehlschlag noch einmal
    // gewählt werden kann (`change` feuert sonst nicht).
    input.value = ''
    if (file) void handle(file)
  })

  retry?.addEventListener('click', () => {
    if (lastFile) void handle(lastFile)
  })

  // `dragover` muss `preventDefault` bekommen, sonst lässt der Browser den
  // Drop nicht zu und öffnet die Datei selbst.
  const over = (event: Event): void => {
    if (!carriesFiles(event)) return
    event.preventDefault()
    if (!busy) root.classList.add(DRAGGING_CLASS)
  }
  zone.addEventListener('dragenter', over)
  zone.addEventListener('dragover', over)
  zone.addEventListener('dragleave', (event) => {
    // Beim Wechsel auf ein Kind feuert `dragleave` auch -- nur der Austritt
    // aus der Fläche selbst zählt.
    const next = (event as DragEvent).relatedTarget
    if (next instanceof Node && zone.contains(next)) return
    root.classList.remove(DRAGGING_CLASS)
  })
  zone.addEventListener('drop', (event) => {
    if (!carriesFiles(event)) return
    event.preventDefault()
    root.classList.remove(DRAGGING_CLASS)
    const file = droppedFile(event)
    if (file) void handle(file)
  })

  if (fallback) fallback.hidden = true
  setState('idle')
}

/** Der Einstieg aus dem Entrypoint: findet auf jeder Seite außer der Startseite nichts. */
export function startGuestUpload(root: Document = document): void {
  const element = root.querySelector<HTMLElement>(GUEST_UPLOAD_SELECTOR)
  if (element) bindGuestUpload(element)
}
